
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { apiClient } from '@/lib/api-client'
import type {
  DietPlanV2,
  CreateDietPlanV2Data,
  UpdateDietPlanV2Data,
  DietCalendarAssignment,
  CreateCalendarAssignmentData,
  FoodItem,
  DietDay,
} from '@/types/diet-v2'

// Sanitize payload before create: strip server-assigned IDs and calculated fields
function sanitizeDietPlanPayload(data: CreateDietPlanV2Data): CreateDietPlanV2Data {
  const payload: CreateDietPlanV2Data = {
    name: data.name,
    type: data.type,
    duration: data.duration,
    ...(data.description !== undefined && data.description !== '' && { description: data.description }),
    ...(data.isTemplate !== undefined && { isTemplate: data.isTemplate }),
  }

  if (data.days && data.days.length > 0) {
    payload.days = data.days.map((day) => {
      const sanitizedDay: { dayNumber: number; date?: string; meals?: unknown[] } = {
        dayNumber: day.dayNumber,
        ...(day.date && { date: day.date }),
      }

      if (day.meals && day.meals.length > 0) {
        sanitizedDay.meals = day.meals.map((meal: { type: string; time?: string; notes?: string; foods?: FoodItem[] }) => {
          const sanitizedMeal: { type: string; time?: string; notes?: string; foods?: unknown[] } = {
            type: meal.type,
            ...(meal.time && { time: meal.time }),
            ...(meal.notes !== undefined && meal.notes !== '' && { notes: meal.notes }),
          }

          if (meal.foods && meal.foods.length > 0) {
            sanitizedMeal.foods = meal.foods.map((food: FoodItem & { _id?: string }) => {
              const source = (food.source === 'usda' || food.source === 'custom' || food.source === 'ai') ? food.source : 'ai'
              const foodPayload: Record<string, unknown> = {
                name: food.name,
                source,
                servingSize: food.servingSize,
                servingUnit: food.servingUnit,
                calories: food.calories,
                macros: {
                  protein: food.macros.protein,
                  carbs: food.macros.carbs,
                  fat: food.macros.fat,
                  fiber: food.macros.fiber ?? 0,
                },
              }
              if (source === 'usda' && food.fdcId) foodPayload.fdcId = food.fdcId
              if (food.customData) foodPayload.customData = food.customData
              return foodPayload
            })
          }

          return sanitizedMeal
        })
      }

      return sanitizedDay as Omit<DietDay, 'totalCalories' | 'macros'>
    }) as CreateDietPlanV2Data['days']
  }

  return payload as CreateDietPlanV2Data
}

// ============================================
// Diet Plans
// ============================================

// Get all Diet Plans
export function useDietPlansV2(filters?: { 
  duration?: string
  type?: string
  isTemplate?: boolean
  page?: number
  limit?: number
}) {
  return useQuery<{ data: DietPlanV2[]; total: number; page: number; limit: number; pages: number }>({
    queryKey: ['dietPlansV2', filters],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.duration) params.append('duration', filters.duration)
      if (filters?.type) params.append('type', filters.type)
      if (filters?.isTemplate !== undefined) params.append('isTemplate', String(filters.isTemplate))
      if (filters?.page) params.append('page', String(filters.page))
      if (filters?.limit) params.append('limit', String(filters.limit))

      const response = await apiClient.get(`/diet-v2/plans?${params.toString()}`)
      
      // Backend returns { plans: [...], total, page, pages }
      // Normalize to { data: [...], total, page, pages }
      if (response.data.plans) {
        return {
          data: response.data.plans,
          total: response.data.total || 0,
          page: response.data.page || 1,
          limit: response.data.limit || 10,
          pages: response.data.pages || 1
        }
      }
      
      return response.data
    },
  })
}

// Get single Diet Plan
export function useDietPlanV2(id: string) {
  return useQuery<DietPlanV2>({
    queryKey: ['dietPlanV2', id],
    queryFn: async () => {
      const response = await apiClient.get(`/diet-v2/plans/${id}`)
      return response.data
    },
    enabled: !!id,
  })
}

// Create Diet Plan
export function useCreateDietPlanV2() {
  const queryClient = useQueryClient()

  return useMutation<DietPlanV2, Error, CreateDietPlanV2Data>({
    mutationFn: async (data) => {
      const sanitized = sanitizeDietPlanPayload(data)
      const response = await apiClient.post('/diet-v2/plans', sanitized)
      return response.data
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Diet plan created successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['dietPlansV2'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create diet plan',
        variant: 'destructive',
      })
    },
  })
}

// Update Diet Plan
export function useUpdateDietPlanV2() {
  const queryClient = useQueryClient()

  return useMutation<DietPlanV2, Error, { id: string } & Partial<UpdateDietPlanV2Data>>({
    mutationFn: async ({ id, ...data }) => {
      const response = await apiClient.patch(`/diet-v2/plans/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Diet plan updated successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['dietPlansV2'] })
      queryClient.invalidateQueries({ queryKey: ['dietPlanV2'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update diet plan',
        variant: 'destructive',
      })
    },
  })
}

// Delete Diet Plan
export function useDeleteDietPlanV2() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/diet-v2/plans/${id}`)
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Diet plan deleted successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['dietPlansV2'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete diet plan',
        variant: 'destructive',
      })
    },
  })
}

// Clone Diet Plan
export function useCloneDietPlanV2() {
  const queryClient = useQueryClient()

  return useMutation<DietPlanV2, Error, { id: string; name?: string }>({
    mutationFn: async ({ id, name }) => {
      const response = await apiClient.post(`/diet-v2/plans/duplicate/${id}`, { name })
      return response.data
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Diet plan cloned successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['dietPlansV2'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to clone diet plan',
        variant: 'destructive',
      })
    },
  })
}

// Generate AI Diet Plan
export function useGenerateDietPlanV2WithAI() {
  const queryClient = useQueryClient()

  return useMutation<DietPlanV2, Error, { 
    prompt: string
    profileId?: string
  }>({
    mutationFn: async (data) => {
      // Increase timeout to 60 seconds for AI generation
      const response = await apiClient.post('/diet-v2/plans/generate-with-ai', data, {
        timeout: 60000
      })
      return response.data
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'AI diet plan generated successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['dietPlansV2'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to generate diet plan with AI. This may take longer than usual.',
        variant: 'destructive',
      })
    },
  })
}

// ============================================
// DIET CALENDAR ASSIGNMENTS
// ============================================

// Get calendar assignments for a user
export function useUserCalendarAssignments(userId: string, status?: string) {
  return useQuery<DietCalendarAssignment[]>({
    queryKey: ['calendarAssignments', userId, status],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (status) params.append('status', status)
      
      const response = await apiClient.get(`/diet-calendar/user/${userId}?${params.toString()}`)
      
      // Backend may return { assignments: [...] } or direct array
      if (response.data.assignments) {
        return response.data.assignments
      }
      
      return Array.isArray(response.data) ? response.data : (response.data?.data || [])
    },
    enabled: !!userId,
  })
}

// Get all calendar assignments
export function useAllCalendarAssignments(status?: string) {
  return useQuery<DietCalendarAssignment[]>({
    queryKey: ['calendarAssignments', 'all', status],
    queryFn: async () => {
      const params = new URLSearchParams()
      if (status) params.append('status', status)
      
      const response = await apiClient.get(`/diet-calendar/assignments?${params.toString()}`)
      
      // Backend returns { assignments: [...], total, page, pages }
      // Normalize to array
      if (response.data.assignments) {
        return response.data.assignments
      }
      
      return Array.isArray(response.data) ? response.data : (response.data?.data || [])
    },
  })
}

// Get single assignment details
export function useCalendarAssignment(assignmentId: string) {
  return useQuery<DietCalendarAssignment>({
    queryKey: ['calendarAssignment', assignmentId],
    queryFn: async () => {
      const response = await apiClient.get(`/diet-calendar/assignment/${assignmentId}`)
      return response.data
    },
    enabled: !!assignmentId,
  })
}

// Create calendar assignment
export function useCreateCalendarAssignment() {
  const queryClient = useQueryClient()

  return useMutation<DietCalendarAssignment, Error, CreateCalendarAssignmentData>({
    mutationFn: async (data) => {
      const response = await apiClient.post('/diet-calendar/assign', data)
      return response.data
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Diet plan assigned successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['calendarAssignments'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to assign diet plan',
        variant: 'destructive',
      })
    },
  })
}

// Update calendar assignment status
export function useUpdateCalendarAssignment() {
  const queryClient = useQueryClient()

  return useMutation<DietCalendarAssignment, Error, { 
    id: string
    status?: 'active' | 'completed' | 'cancelled'
    notes?: string 
  }>({
    mutationFn: async ({ id, ...data }) => {
      const response = await apiClient.patch(`/diet-calendar/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Assignment updated successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['calendarAssignments'] })
      queryClient.invalidateQueries({ queryKey: ['calendarAssignment'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update assignment',
        variant: 'destructive',
      })
    },
  })
}

// Delete/Cancel calendar assignment
export function useDeleteCalendarAssignment() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      await apiClient.delete(`/diet-calendar/${id}`)
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Assignment cancelled successfully',
      })
      queryClient.invalidateQueries({ queryKey: ['calendarAssignments'] })
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to cancel assignment',
        variant: 'destructive',
      })
    },
  })
}

// Backward compatibility aliases
export const useDuplicateDietPlanV2 = useCloneDietPlanV2
